/**
 * Example preview for two-photo templates (Family reunions, Sunset Couple,
 * Hug Your Younger Self): "photo + photo → result", so users see that both
 * uploads end up in one picture. A plain Before/After slider only showed one
 * of the inputs and read as if the result came from a single photo.
 *
 *   html: twoPhotoExampleHtml({ inputs: [{ url, label }, { url, label }], result })
 */
import { escapeHtml } from './placeholder.js';
import { icon } from './icon.js';

/** Labels and hints for template upload slots (editorSchema.inputs names). */
export const KNOWN_INPUTS: Record<string, { label: string; hint: string }> = {
  photo: { label: 'Your photo', hint: 'Clear, front-facing, good lighting' },
  face: { label: 'Face photo', hint: 'One clear face, looking at the camera' },
  childhood: { label: 'Childhood photo', hint: 'You as a child, face clearly visible' },
  partner: { label: "Partner's photo", hint: 'Clear, front-facing, good lighting' },
  loved: { label: "Loved one's photo", hint: 'Only photos of your own family; old or black-and-white is fine' },
  pet: { label: "Your pet's photo", hint: 'One pet, face clearly visible' },
};

/** Upload-slot labels of a template, in order (defaults to one "Your photo"). */
export function inputLabels(editorSchema: Record<string, unknown> | undefined): string[] {
  const raw = editorSchema?.['inputs'];
  const names = Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [];
  return (names.length > 0 ? names : ['photo']).map(
    (n) => KNOWN_INPUTS[n]?.label ?? n.charAt(0).toUpperCase() + n.slice(1),
  );
}

export interface TwoPhotoExampleOptions {
  inputs: [{ url: string; label: string }, { url: string; label: string }];
  result: string;
  className?: string;
}

export function twoPhotoExampleHtml(o: TwoPhotoExampleOptions): string {
  const fig = (url: string, label: string, cls = ''): string => `
      <figure${cls ? ` class="${cls}"` : ''}>
        <img src="${escapeHtml(url)}" alt="" decoding="async" />
        <figcaption>${escapeHtml(label)}</figcaption>
      </figure>`;
  const [a, b] = o.inputs;
  return `
    <section class="tpx${o.className ? ` ${o.className}` : ''}" aria-label="Example: ${escapeHtml(a.label)} plus ${escapeHtml(b.label)} make one result">
      <div class="tpx-ins">
        ${fig(a.url, a.label)}
        <span class="tpx-plus" aria-hidden="true">${icon('add')}</span>
        ${fig(b.url, b.label)}
      </div>
      <span class="tpx-arrow" aria-hidden="true">${icon('arrow_forward')}</span>
      ${fig(o.result, 'Result', 'tpx-out')}
    </section>`;
}
